import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CITIES, generateWeather } from './weatherData';
import './Weather.css';

function Weather() {
  const navigate = useNavigate();
  const [selectedCity, setSelectedCity] = useState('new-york');
  const [unit, setUnit] = useState('C');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const weather = useMemo(() => generateWeather(selectedCity), [selectedCity]);

  const toUnit = (c) => (unit === 'F' ? Math.round(c * 9 / 5 + 32) : c);
  const unitLabel = unit === 'C' ? '°C' : '°F';

  const filteredCities = CITIES.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getAqiLabel = (aqi) => {
    if (aqi <= 50) return { text: 'Good', color: '#00c853' };
    if (aqi <= 100) return { text: 'Moderate', color: '#ffab00' };
    return { text: 'Unhealthy', color: '#ff4757' };
  };

  const getUvLabel = (uv) => {
    if (uv <= 2) return 'Low';
    if (uv <= 5) return 'Moderate';
    if (uv <= 7) return 'High';
    return 'Very High';
  };

  if (!weather) return null;

  const aqi = getAqiLabel(weather.airQuality);
  const currentHour = new Date().getHours();

  return (
    <div className="weather-app">
      <div className="weather-topbar">
        <div className="weather-topbar-left">
          <button className="weather-back-btn" onClick={() => navigate('/')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="weather-title">Weather Dashboard</h1>
        </div>
        <div className="weather-topbar-right">
          <div className="weather-city-selector">
            <button className="weather-search-toggle" onClick={() => setShowSearch(!showSearch)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
              {weather.city.name}, {weather.city.country}
            </button>
            {showSearch && (
              <div className="weather-search-dropdown">
                <input
                  type="text"
                  placeholder="Search cities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                <div className="weather-search-list">
                  {filteredCities.map((city) => (
                    <button
                      key={city.id}
                      className={`weather-search-item ${selectedCity === city.id ? 'active' : ''}`}
                      onClick={() => { setSelectedCity(city.id); setShowSearch(false); setSearchQuery(''); }}
                    >
                      <span>{city.name}</span>
                      <span className="weather-search-country">{city.country}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="weather-unit-toggle">
            <button className={unit === 'C' ? 'active' : ''} onClick={() => setUnit('C')}>°C</button>
            <button className={unit === 'F' ? 'active' : ''} onClick={() => setUnit('F')}>°F</button>
          </div>
        </div>
      </div>

      <div className="weather-content">
        {/* Main Card */}
        <div className="weather-main-card" style={{ background: weather.condition.bg }}>
          <div className="weather-main-left">
            <div className="weather-main-icon">{weather.condition.icon}</div>
            <div className="weather-main-temp">{toUnit(weather.temp)}{unitLabel}</div>
            <div className="weather-main-condition">{weather.condition.label}</div>
            <div className="weather-main-feels">Feels like {toUnit(weather.feelsLike)}{unitLabel}</div>
          </div>
          <div className="weather-main-right">
            <div className="weather-main-city">{weather.city.name}</div>
            <div className="weather-main-date">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="weather-stats">
          <div className="weather-stat-card">
            <div className="weather-stat-icon">💧</div>
            <div className="weather-stat-info">
              <span className="weather-stat-value">{weather.humidity}%</span>
              <span className="weather-stat-label">Humidity</span>
            </div>
          </div>
          <div className="weather-stat-card">
            <div className="weather-stat-icon">🌬️</div>
            <div className="weather-stat-info">
              <span className="weather-stat-value">{weather.windSpeed} km/h</span>
              <span className="weather-stat-label">Wind Speed</span>
            </div>
          </div>
          <div className="weather-stat-card">
            <div className="weather-stat-icon">🌡️</div>
            <div className="weather-stat-info">
              <span className="weather-stat-value">{weather.pressure} hPa</span>
              <span className="weather-stat-label">Pressure</span>
            </div>
          </div>
          <div className="weather-stat-card">
            <div className="weather-stat-icon">👁️</div>
            <div className="weather-stat-info">
              <span className="weather-stat-value">{weather.visibility} km</span>
              <span className="weather-stat-label">Visibility</span>
            </div>
          </div>
          <div className="weather-stat-card">
            <div className="weather-stat-icon">☀️</div>
            <div className="weather-stat-info">
              <span className="weather-stat-value">{weather.uvIndex} - {getUvLabel(weather.uvIndex)}</span>
              <span className="weather-stat-label">UV Index</span>
            </div>
            <div className="weather-uv-bar">
              <div className="weather-uv-fill" style={{ width: `${(weather.uvIndex / 11) * 100}%` }}></div>
            </div>
          </div>
          <div className="weather-stat-card">
            <div className="weather-stat-icon">🫁</div>
            <div className="weather-stat-info">
              <span className="weather-stat-value" style={{ color: aqi.color }}>{weather.airQuality} - {aqi.text}</span>
              <span className="weather-stat-label">Air Quality</span>
            </div>
          </div>
          <div className="weather-stat-card">
            <div className="weather-stat-icon">🌅</div>
            <div className="weather-stat-info">
              <span className="weather-stat-value">{weather.sunrise}</span>
              <span className="weather-stat-label">Sunrise</span>
            </div>
          </div>
          <div className="weather-stat-card">
            <div className="weather-stat-icon">🌇</div>
            <div className="weather-stat-info">
              <span className="weather-stat-value">{weather.sunset}</span>
              <span className="weather-stat-label">Sunset</span>
            </div>
          </div>
        </div>

        {/* Hourly */}
        <div className="weather-section">
          <h2 className="weather-section-title">Hourly Forecast</h2>
          <div className="weather-hourly">
            {weather.hourly.map((h, i) => (
              <div key={i} className={`weather-hourly-item ${h.hour === currentHour ? 'now' : ''}`}>
                <span className="weather-hourly-time">
                  {h.hour === currentHour ? 'Now' : `${h.hour.toString().padStart(2, '0')}:00`}
                </span>
                <span className="weather-hourly-icon">{h.icon}</span>
                <span className="weather-hourly-temp">{toUnit(h.temp)}°</span>
                <div className="weather-hourly-precip">
                  <div className="weather-precip-bar">
                    <div className="weather-precip-fill" style={{ height: `${h.precipitation}%` }}></div>
                  </div>
                  <span className="weather-precip-val">{h.precipitation}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 7-Day */}
        <div className="weather-section">
          <h2 className="weather-section-title">7-Day Forecast</h2>
          <div className="weather-daily">
            {weather.daily.map((d, i) => (
              <div key={i} className={`weather-daily-item ${i === 0 ? 'today' : ''}`}>
                <div className="weather-daily-day">
                  <span className="weather-daily-name">{i === 0 ? 'Today' : d.day}</span>
                  <span className="weather-daily-date">{d.date}</span>
                </div>
                <span className="weather-daily-icon">{d.icon}</span>
                <span className="weather-daily-label">{d.label}</span>
                <div className="weather-daily-temps">
                  <span className="weather-daily-high">{toUnit(d.high)}°</span>
                  <div className="weather-temp-range">
                    <div
                      className="weather-temp-range-fill"
                      style={{
                        left: `${((d.low - (weather.temp - 15)) / 30) * 100}%`,
                        right: `${100 - ((d.high - (weather.temp - 15)) / 30) * 100}%`,
                      }}
                    ></div>
                  </div>
                  <span className="weather-daily-low">{toUnit(d.low)}°</span>
                </div>
                <div className="weather-daily-precip">
                  💧 {d.precipitation}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Weather;
